import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/axiosConfig";

const BASE = "/api/faqs";

// Extrae un mensaje de error legible del envelope { success, data, message }.
const msgError = (e) =>
  e?.response?.data?.message || e?.message || "Ocurrió un error inesperado.";

// ------- Lecturas -------

// Público: FAQ activas CON canvas (para renderizar). Requiere ?full=true en el backend.
export const fetchFAQsPublicas = createAsyncThunk(
  "faq/fetchPublicas",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}?full=true`);
      return Array.isArray(data?.data) ? data.data : [];
    } catch (e) {
      return rejectWithValue(msgError(e));
    }
  },
);

// Admin: listado (sin canvas, liviano) para la tabla de administración.
export const fetchFAQsAdmin = createAsyncThunk(
  "faq/fetchAdmin",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}?admin=true`);
      return Array.isArray(data?.data) ? data.data : [];
    } catch (e) {
      return rejectWithValue(msgError(e));
    }
  },
);

// Admin: una FAQ COMPLETA (con canvas) para editar.
export const fetchFAQById = createAsyncThunk(
  "faq/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/${id}?admin=true`);
      return data?.data || null;
    } catch (e) {
      return rejectWithValue(msgError(e));
    }
  },
);

// ------- Mutaciones -------

export const crearFAQ = createAsyncThunk(
  "faq/crear",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(BASE, payload);
      return data?.data;
    } catch (e) {
      return rejectWithValue({
        message: e?.response?.data?.message || "No se pudo crear la FAQ.",
        errors: e?.response?.data?.errors || [],
        status: e?.response?.status,
      });
    }
  },
);

export const actualizarFAQ = createAsyncThunk(
  "faq/actualizar",
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`${BASE}/${id}`, payload);
      return data?.data;
    } catch (e) {
      // 409 = conflicto de versión (optimistic locking)
      return rejectWithValue({
        message: msgError(e),
        status: e?.response?.status,
      });
    }
  },
);

export const toggleFAQ = createAsyncThunk(
  "faq/toggle",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${BASE}/${id}/status`);
      return data?.data; // { id, estado_activo }
    } catch (e) {
      return rejectWithValue(msgError(e));
    }
  },
);

export const eliminarFAQ = createAsyncThunk(
  "faq/eliminar",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${BASE}/${id}`);
      return id;
    } catch (e) {
      return rejectWithValue(msgError(e));
    }
  },
);

// Carga de media del canvas (imágenes). Devuelve { url, tipo }.
export const subirMediaFAQ = createAsyncThunk(
  "faq/subirMedia",
  async (file, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("media", file); // el backend espera exactamente el campo "media"
      const { data } = await api.post(`${BASE}/upload-media`, fd);
      return data?.data; // { url, tipo }
    } catch (e) {
      return rejectWithValue(msgError(e));
    }
  },
);

// ── PAPELERA (issue #12) ──
export const fetchPapelera = createAsyncThunk(
  "faq/fetchPapelera",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/papelera`);
      return data?.data || [];
    } catch (e) {
      return rejectWithValue({
        message: e?.response?.data?.message || "No se pudo cargar la papelera.",
        status: e?.response?.status,
      });
    }
  },
);

export const restaurarFAQ = createAsyncThunk(
  "faq/restaurar",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${BASE}/${id}/restore`);
      return data?.data || { id };
    } catch (e) {
      return rejectWithValue({
        message: e?.response?.data?.message || "No se pudo restaurar la FAQ.",
        status: e?.response?.status,
      });
    }
  },
);

export const eliminarDefinitivoFAQ = createAsyncThunk(
  "faq/eliminarDefinitivo",
  async (id, { rejectWithValue }) => {
    try {
      // OJO: el backend usa DELETE /:id/force. Si LiteSpeed bloquea DELETE
      // (como bloqueó PATCH), habrá que exponer una ruta POST equivalente.
      await api.delete(`${BASE}/${id}/force`);
      return id;
    } catch (e) {
      return rejectWithValue({
        message:
          e?.response?.data?.message || "No se pudo eliminar definitivamente.",
        status: e?.response?.status,
      });
    }
  },
);

const faqSlice = createSlice({
  name: "faq",
  initialState: {
    publicas: [],
    admin: [],
    seleccionada: null, // FAQ en edición (con canvas)
    loading: false,
    saving: false,
    error: null,
    loadingPapelera: false,
  },
  reducers: {
    limpiarSeleccionada: (state) => {
      state.seleccionada = null;
    },
    limpiarError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Públicas
      .addCase(fetchFAQsPublicas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFAQsPublicas.fulfilled, (state, action) => {
        state.loading = false;
        state.publicas = action.payload;
      })
      .addCase(fetchFAQsPublicas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Admin listado
      .addCase(fetchFAQsAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFAQsAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.admin = action.payload;
      })
      .addCase(fetchFAQsAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Obtener por id (para editar)
      .addCase(fetchFAQById.fulfilled, (state, action) => {
        state.seleccionada = action.payload;
      })
      .addCase(fetchFAQById.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Crear
      .addCase(crearFAQ.pending, (state) => {
        state.saving = true;
      })
      .addCase(crearFAQ.fulfilled, (state, action) => {
        state.saving = false;
        if (action.payload) state.admin.unshift(action.payload);
      })
      .addCase(crearFAQ.rejected, (state) => {
        state.saving = false;
      })

      // Actualizar
      .addCase(actualizarFAQ.pending, (state) => {
        state.saving = true;
      })
      .addCase(actualizarFAQ.fulfilled, (state, action) => {
        state.saving = false;
        const faq = action.payload;
        if (faq) {
          const i = state.admin.findIndex((f) => f.id === faq.id);
          if (i !== -1) state.admin[i] = { ...state.admin[i], ...faq };
        }
      })
      .addCase(actualizarFAQ.rejected, (state) => {
        state.saving = false;
      })

      // Toggle estado
      .addCase(toggleFAQ.fulfilled, (state, action) => {
        const { id, estado_activo } = action.payload || {};
        const i = state.admin.findIndex((f) => f.id === id);
        if (i !== -1) state.admin[i].estado_activo = estado_activo;
      })

      // Eliminar
      .addCase(eliminarFAQ.fulfilled, (state, action) => {
        state.admin = state.admin.filter((f) => f.id !== action.payload);
      })
      .addCase(fetchPapelera.pending, (state) => {
        state.loadingPapelera = true;
      })
      .addCase(fetchPapelera.fulfilled, (state, action) => {
        state.loadingPapelera = false;
        state.papelera = action.payload;
      })
      .addCase(fetchPapelera.rejected, (state) => {
        state.loadingPapelera = false;
      })
      .addCase(restaurarFAQ.fulfilled, (state, action) => {
        state.papelera = state.papelera.filter(
          (f) => f.id !== action.payload.id,
        );
      })
      .addCase(eliminarDefinitivoFAQ.fulfilled, (state, action) => {
        state.papelera = state.papelera.filter((f) => f.id !== action.payload);
      });
  },
});

export const { limpiarSeleccionada, limpiarError } = faqSlice.actions;
export default faqSlice.reducer;
